import Table, { TableColumn } from '@hero/ui-kit/tables/Table';
import { MarkButton, MarkChoice, MarkLabel } from '../../../components/MarkControls';
import React, { useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import H from '@hero/ui-kit/typography/H';
import Section from '@hero/ui-kit/layout/Section';
import useTheme from '@hero/branding/theme';
import * as styles from './style.module.css';
import InlineLoader from '../../../../../components/InlineLoader';
import type { SegmentPreviewResponse, SegmentSample, SegmentTargetKind } from '../../../types';

type Props = {
    data: SegmentPreviewResponse | null;
    isLoading: boolean;
    error: string | null;
    onPreview: () => void;
    disabled: boolean;
    targetKind: SegmentTargetKind | '';
};

type SampleRow = SegmentSample & { row_id: number };
export const SampleTable: React.FC<{
    sample: SegmentSample[];
    targetKind: SegmentTargetKind | '';
    showTechnical: boolean;
}> = ({ sample, targetKind, showTechnical }) => {
    const columns: TableColumn<SampleRow>[] = [
        { colKey: 'profile_id', name: 'Profile' },
        { colKey: 'email', name: 'Email' },
        { colKey: 'first_name', name: 'First' },
        { colKey: 'last_name', name: 'Last' }
    ];
    if (targetKind === 'MEMBERSHIP' || targetKind === 'MEMBERSHIP_ADMIN') {
        columns.push({ colKey: 'membership_id', name: 'Membership' });
    }
    if (targetKind === 'PROSPECT')
        columns.push({ colKey: 'prospect_details_id', name: 'Prospect' });
    if (showTechnical) columns.push({ colKey: 'send_unit_key', name: 'Send unit key' });
    return (
        <div style={{ overflowX: 'auto', marginTop: '0.8rem' }}>
            <Table<SampleRow>
                columns={columns}
                data={sample.map((row, row_id) => ({ ...row, row_id }))}
                rowKey="row_id"
                emptyValues="—"
            />
        </div>
    );
};

const PreviewPanel: React.FC<Props> = ({
    data,
    isLoading,
    error,
    onPreview,
    disabled,
    targetKind
}) => {
    const { colors, borders } = useTheme();
    const [showTechnical, setShowTechnical] = useState(false);
    const isTupleKind =
        targetKind === 'MEMBERSHIP' ||
        targetKind === 'MEMBERSHIP_ADMIN' ||
        targetKind === 'PROSPECT';

    return (
        <Section
            border
            noDefaultPadding
            subtheme={['regular', 'background']}
            className={styles.panel}
            styles={borders.variant.secondary}
        >
            <div className={styles.panelHeader}>
                <div>
                    <H role="h5" styles={{ marginBottom: '0.8rem' }}>
                        Audience preview
                    </H>
                    <P noDefaultMargin size="small" styles={{ color: colors.neutrals.textBeta }}>
                        Check how many records match before saving your segment.
                    </P>
                </div>
                <MarkButton
                    onClick={onPreview}
                    disabled={disabled || isLoading}
                    variant="outlineSecondary"
                >
                    {isLoading ? 'Previewing…' : 'Preview audience'}
                </MarkButton>
            </div>
            {isLoading && (
                <div role="status" aria-label="Loading audience preview">
                    <InlineLoader />
                </div>
            )}
            {error && (
                <P
                    role="alert"
                    noDefaultMargin
                    size="small"
                    styles={{ color: colors.status.fail.alpha }}
                >
                    {error}
                </P>
            )}
            {data && !isLoading && (
                <div aria-live="polite">
                    <H role="h4" styles={{ marginBottom: '0.4rem' }}>
                        {data.match_count.toLocaleString()}
                    </H>
                    <P
                        noDefaultMargin
                        styles={{ color: colors.neutrals.textBeta, marginBottom: '1.6rem' }}
                    >
                        matching records
                    </P>
                    {isTupleKind && (
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{ color: colors.neutrals.textBeta, marginBottom: '1.6rem' }}
                        >
                            A contact can have multiple matching memberships or prospects, so this
                            count may exceed the number of contacts.
                        </P>
                    )}
                    {data.sample.length > 0 ? (
                        <>
                            <div className={styles.panelHeader} style={{ marginBottom: 0 }}>
                                <P noDefaultMargin strong size="small">
                                    Sample of up to 10 matches
                                </P>
                                <MarkLabel
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.8rem',
                                        margin: 0
                                    }}
                                >
                                    <MarkChoice
                                        type="checkbox"
                                        checked={showTechnical}
                                        onChange={(event) =>
                                            setShowTechnical(event.currentTarget.checked)
                                        }
                                    />
                                    Show technical fields
                                </MarkLabel>
                            </div>
                            <SampleTable
                                sample={data.sample}
                                targetKind={targetKind}
                                showTechnical={showTechnical}
                            />
                        </>
                    ) : (
                        <P noDefaultMargin size="small">
                            {data.match_count === 0
                                ? 'No records match these filters. Try adjusting your conditions.'
                                : 'Matching records were found, but no sample is available.'}
                        </P>
                    )}
                </div>
            )}
            {!data && !isLoading && !error && (
                <P noDefaultMargin size="small" styles={{ color: colors.neutrals.textBeta }}>
                    {disabled
                        ? 'Add a complete filter to preview your audience.'
                        : 'Your filters are ready. Preview the audience to see matching records.'}
                </P>
            )}
        </Section>
    );
};

export default PreviewPanel;
